import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';

// Types
export type IuguAmbiente = 'TESTE' | 'PRODUCAO';

export interface IuguConfig {
  id: string;
  ambiente: IuguAmbiente;
  ativo: boolean;
  accountId?: string | null;
  webhookUrl?: string | null;
  enableCreditCard?: boolean | null;
  enablePix?: boolean | null;
  enableBankSlip?: boolean | null;
  invoicePrefix?: string | null;
  defaultDueDays?: number | null;
  lateFinePercent?: number | null;
  monthlyInterestPercent?: number | null;
  notificationUrl?: string | null;
  criadoEm?: string | null;
  atualizadoEm?: string | null;
  apiTokenMasked?: string | null;
  webhookSecretMasked?: string | null;
}

export interface IuguConfigInput {
  ambiente: IuguAmbiente;
  ativo?: boolean | null;
  accountId?: string | null;
  webhookUrl?: string | null;
  enableCreditCard?: boolean | null;
  enablePix?: boolean | null;
  enableBankSlip?: boolean | null;
  invoicePrefix?: string | null;
  defaultDueDays?: number | null;
  lateFinePercent?: number | null;
  monthlyInterestPercent?: number | null;
  notificationUrl?: string | null;
  apiToken?: string | null;        // enviar null se usuário não digitar nada
  webhookSecret?: string | null;   // enviar null se usuário não digitar nada
}

export interface IuguConfigConnection {
  items: IuguConfig[];
  page: number | null;
  size: number | null;
  totalItems: number | null;
  totalPages: number | null;
}

// Fragments (to keep fields consistent)
const IUGU_CONFIG_FIELDS = gql`
  fragment IuguConfigFields on IuguConfig {
    id
    ambiente
    ativo
    accountId
    webhookUrl
    enableCreditCard
    enablePix
    enableBankSlip
    invoicePrefix
    defaultDueDays
    lateFinePercent
    monthlyInterestPercent
    notificationUrl
    criadoEm
    atualizadoEm
    apiTokenMasked
    webhookSecretMasked
  }
`;

// Queries
export const IUGU_CONFIGS_QUERY = gql`
  ${IUGU_CONFIG_FIELDS}
  query IuguConfigs($page: Int, $size: Int, $somenteAtivas: Boolean, $ambiente: IuguAmbiente) {
    iuguConfigs(page: $page, size: $size, somenteAtivas: $somenteAtivas, ambiente: $ambiente) {
      items { ...IuguConfigFields }
      page
      size
      totalItems
      totalPages
    }
  }
`;

// Mutations
export const CRIAR_IUGU_CONFIG = gql`
  ${IUGU_CONFIG_FIELDS}
  mutation CriarIuguConfig($input: IuguConfigInput!) {
    criarIuguConfig(input: $input) { ...IuguConfigFields }
  }
`;

export const ATUALIZAR_IUGU_CONFIG = gql`
  ${IUGU_CONFIG_FIELDS}
  mutation AtualizarIuguConfig($id: ID!, $input: IuguConfigInput!) {
    atualizarIuguConfig(id: $id, input: $input) { ...IuguConfigFields }
  }
`;

export const ATIVAR_IUGU_CONFIG = gql`
  mutation AtivarIuguConfig($id: ID!) {
    ativarIuguConfig(id: $id)
  }
`;

// Service API
export async function fetchAll(): Promise<IuguConfig[]> {
  const { data } = await apolloClient.query<{ iuguConfigs: IuguConfigConnection }>({
    query: IUGU_CONFIGS_QUERY,
    variables: { page: 0, size: 50 },
    fetchPolicy: 'no-cache',
  });
  return data.iuguConfigs.items || [];
}

export async function save(
  env: 'TESTE' | 'PRODUCAO',
  payload: Omit<IuguConfigInput, 'ambiente'> & { ambiente?: IuguAmbiente },
  id?: string,
): Promise<IuguConfig> {
  // Ensure ambiente is set from the env parameter
  const input: IuguConfigInput = {
    ...payload,
    ambiente: env,
    // Normalize optional numeric fields: undefined -> null when appropriate
    defaultDueDays: payload.defaultDueDays ?? null,
    lateFinePercent: payload.lateFinePercent ?? null,
    monthlyInterestPercent: payload.monthlyInterestPercent ?? null,
    // Secrets: if empty string, send null to not alter
    apiToken: payload.apiToken === '' ? null : payload.apiToken ?? null,
    webhookSecret: payload.webhookSecret === '' ? null : payload.webhookSecret ?? null,
  };

  if (id) {
    const { data } = await apolloClient.mutate<{ atualizarIuguConfig: IuguConfig }, { id: string; input: IuguConfigInput }>(
      {
        mutation: ATUALIZAR_IUGU_CONFIG,
        variables: { id, input },
      },
    );
    if (!data?.atualizarIuguConfig) throw new Error('Falha ao atualizar configuração IUGU.');
    return data.atualizarIuguConfig;
  }

  const { data } = await apolloClient.mutate<{ criarIuguConfig: IuguConfig }, { input: IuguConfigInput }>({
    mutation: CRIAR_IUGU_CONFIG,
    variables: { input },
  });
  if (!data?.criarIuguConfig) throw new Error('Falha ao criar configuração IUGU.');
  return data.criarIuguConfig;
}

export async function setActive(id: string): Promise<boolean> {
  const { data } = await apolloClient.mutate<{ ativarIuguConfig: boolean }, { id: string }>({
    mutation: ATIVAR_IUGU_CONFIG,
    variables: { id },
  });
  return Boolean(data?.ativarIuguConfig);
}
